/**
 * TransferPage Component
 * 앱웹 전용 송금 페이지 - VROONG 브랜드 통일 디자인
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNavigation from '../../../../core/components/BottomNavigation';
import { useTransfer } from '../hooks/useTransfer';
import { useMaintenanceGuard } from '../../../system/maintenance/hooks/useMaintenanceGuard';
import styles from '../styles/TransferPage.module.css';

const TransferPage: React.FC = () => {
    const navigate = useNavigate();
    const {
        accountInfo,
        targets,
        selectedTarget,
        setSelectedTarget,
        isLoading,
        error,
        isSearching,
        isTransferring,
        transferError,
        fetchAccountInfo,
        searchTargets,
        handleTransfer,
        clearSelectedTarget,
    } = useTransfer();
    const { canProceed } = useMaintenanceGuard();

    const [transferAmount, setTransferAmount] = useState<string>('');
    const [briefs, setBriefs] = useState<string>('');
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // 검색어 변경 시 debounce 적용하여 실시간 검색
    useEffect(() => {
        // 이전 타이머 취소
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // 300ms 후에 검색 실행
        debounceTimerRef.current = setTimeout(() => {
            searchTargets(searchKeyword);
        }, 300);

        // cleanup
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchKeyword, searchTargets]);

    // 입력값에서 숫자만 추출
    const parseAmount = (value: string): number => {
        const numbers = value.replace(/[^0-9]/g, '');
        return numbers ? parseInt(numbers, 10) : 0;
    };

    // 금액 포맷팅 (천단위 콤마)
    const formatAmount = (amount: number | null | undefined): string => {
        if (amount === null || amount === undefined) {
            return '0';
        }
        return amount.toLocaleString();
    };

    // 송금 금액 입력 핸들러
    const handleTransferAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const numbers = value.replace(/[^0-9]/g, '');
        if (numbers) {
            const amount = parseInt(numbers, 10);
            setTransferAmount(formatAmount(amount));
        } else {
            setTransferAmount('');
        }
    };

    // 검색어 입력 핸들러
    const handleSearchKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchKeyword(e.target.value);
    };

    // 대상 선택 핸들러
    const handleTargetSelect = (target: typeof targets[0]) => {
        setSelectedTarget(target);
    };

    // 송금 계산된 값들
    const requestAmount = parseAmount(transferAmount); // 송금금액
    const availableBalance = accountInfo?.balAmt || 0; // 송금가능 잔액

    // 송금 버튼 비활성화 조건
    const isTransferDisabled = isTransferring ||
        requestAmount <= 0 ||
        availableBalance <= 0 ||
        requestAmount > availableBalance ||
        !selectedTarget;

    // 송금 신청 핸들러
    const handleTransferSubmit = async () => {
        if (!selectedTarget) {
            alert('송금 대상을 선택해주세요.');
            return;
        }

        if (requestAmount <= 0) {
            alert('송금 금액을 입력해주세요.');
            return;
        }

        if (accountInfo && requestAmount > accountInfo.balAmt) {
            alert('송금 가능 잔액을 초과할 수 없습니다.');
            return;
        }

        // 송금 전 점검 상태 즉시 확인
        const canTransfer = await canProceed();
        if (!canTransfer) {
            alert('시스템 점검 중입니다. 잠시 후 다시 시도해주세요.');
            return;
        }

        const result = await handleTransfer(
            requestAmount,
            selectedTarget.targetId,
            selectedTarget.targetType,
            briefs
        );

        if (result && result.success) {
            alert(result.message || '송금이 완료되었습니다.');
            // 성공 시 입력 필드 초기화
            setTransferAmount('');
            setBriefs('');
            clearSelectedTarget();
            // 계좌 정보 다시 조회 (잔액 업데이트)
            await fetchAccountInfo();
        } else if (result && !result.success) {
            alert(result.message || '송금에 실패했습니다.');
        }
    };

    // 대상 타입 라벨
    const getTargetTypeLabel = (type: 'R' | 'S') => {
        return type === 'R' ? '라이더' : '협력사';
    };

    // 로딩 상태
    if (isLoading) {
        return (
            <div className={`screen active ${styles['transfer-screen']}`}>
                <div className={styles['transfer-header']}>
                    <div className={styles['header-top']}>
                        <h1 className={styles['page-title']}>송금</h1>
                    </div>
                </div>
                <div className={styles['transfer-container']}>
                    <div className={styles['loading-section']}>
                        <div className={styles['loading-spinner']}></div>
                        <p className={styles['loading-text']}>정보를 불러오는 중...</p>
                    </div>
                </div>
                <BottomNavigation currentScreen="transfer" />
            </div>
        );
    }

    // 에러 상태
    if (error) {
        return (
            <div className={`screen active ${styles['transfer-screen']}`}>
                <div className={styles['transfer-header']}>
                    <div className={styles['header-top']}>
                        <h1 className={styles['page-title']}>송금</h1>
                    </div>
                </div>
                <div className={styles['transfer-container']}>
                    <div className={styles['error-section']}>
                        <div className={styles['error-icon']}>⚠️</div>
                        <p className={styles['error-text']}>
                            {error.message || '정보를 불러오는데 실패했습니다.'}
                        </p>
                    </div>
                </div>
                <BottomNavigation currentScreen="transfer" />
            </div>
        );
    }

    return (
        <div className={`screen active ${styles['transfer-screen']}`}>
            {/* 헤더 */}
            <div className={styles['transfer-header']}>
                <div className={styles['header-top']}>
                    <h1 className={styles['page-title']}>송금</h1>
                </div>
            </div>

            {/* 메인 컨텐츠 */}
            <div className={styles['transfer-container']}>
                {/* 잔액 정보 카드 */}
                <div className={styles['balance-card']}>
                    <div className={styles['balance-label']}>
                        <span className={styles['balance-icon']}>💳</span>
                        송금가능 잔액
                    </div>
                    <div className={styles['balance-value']}>
                        {accountInfo ? formatAmount(accountInfo.balAmt) : '0'}
                        <span className={styles['balance-unit']}>원</span>
                    </div>
                    <div className={styles['fee-notice']}>
                        💡 송금시 수수료가 부과되지 않습니다
                    </div>
                </div>

                {/* 송금 폼 섹션 */}
                <div className={styles['transfer-form-section']}>
                    <h2 className={styles['section-title']}>
                        <span className={styles['section-title-icon']}>🎯</span>
                        송금 대상 선택
                    </h2>

                    {/* 대상 검색 */}
                    <div className={styles['target-selection']}>
                        <div className={styles['target-input-wrapper']}>
                            <label className={styles['target-input-label']}>대상 검색</label>
                            <input
                                type="text"
                                placeholder="이름 또는 ID로 검색"
                                className={styles['target-input']}
                                value={searchKeyword}
                                onChange={handleSearchKeywordChange}
                            />
                            {isSearching && (
                                <div className={styles['searching-indicator']}>검색 중...</div>
                            )}
                        </div>

                        {/* 대상 목록 */}
                        {targets.length > 0 && (
                            <div className={styles['target-list']}>
                                {targets.map((target) => (
                                    <div
                                        key={`${target.targetType}-${target.targetId}`}
                                        className={`${styles['target-item']} ${
                                            selectedTarget?.targetId === target.targetId &&
                                            selectedTarget?.targetType === target.targetType
                                                ? styles['selected']
                                                : ''
                                        }`}
                                        onClick={() => handleTargetSelect(target)}
                                    >
                                        <div className={styles['target-info']}>
                                            <div className={styles['target-name']}>{target.targetName}</div>
                                            <div className={styles['target-id']}>{target.targetId}</div>
                                        </div>
                                        <span
                                            className={`${styles['target-type-badge']} ${
                                                target.targetType === 'R' ? styles['rider'] : styles['supplier']
                                            }`}
                                        >
                                            {getTargetTypeLabel(target.targetType)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* 대상 없음 */}
                        {!isSearching && targets.length === 0 && searchKeyword && (
                            <div className={styles['no-targets']}>
                                검색 결과가 없습니다
                            </div>
                        )}

                        {/* 선택된 대상 표시 */}
                        {selectedTarget && (
                            <div className={styles['selected-target-card']}>
                                <div className={styles['selected-target-info']}>
                                    <div className={styles['selected-target-icon']}>
                                        {selectedTarget.targetType === 'R' ? '🏍️' : '🏢'}
                                    </div>
                                    <div className={styles['selected-target-details']}>
                                        <span className={styles['selected-target-name']}>
                                            {selectedTarget.targetName}
                                        </span>
                                        <span className={styles['selected-target-id']}>
                                            {selectedTarget.targetId} ({getTargetTypeLabel(selectedTarget.targetType)})
                                        </span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className={styles['clear-target-btn']}
                                    onClick={clearSelectedTarget}
                                >
                                    ✕
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* 금액 입력 섹션 */}
                <div className={styles['transfer-form-section']}>
                    <h2 className={styles['section-title']}>
                        <span className={styles['section-title-icon']}>💸</span>
                        송금 정보
                    </h2>

                    {/* 금액 입력 */}
                    <div className={styles['amount-input-wrapper']}>
                        <label className={styles['amount-input-label']}>송금 금액</label>
                        <input
                            type="text"
                            placeholder="0 원"
                            className={styles['amount-input']}
                            value={transferAmount}
                            onChange={handleTransferAmountChange}
                        />
                    </div>

                    {/* 비고 입력 */}
                    <div className={styles['briefs-input-wrapper']}>
                        <label className={styles['briefs-input-label']}>비고 (선택)</label>
                        <input
                            type="text"
                            placeholder="송금 메모를 입력하세요"
                            className={styles['briefs-input']}
                            value={briefs}
                            onChange={(e) => setBriefs(e.target.value)}
                        />
                    </div>

                    {/* 계산 상세 */}
                    <div className={styles['calculation-card']}>
                        <div className={styles['calc-row']}>
                            <span className={styles['calc-label']}>송금금액</span>
                            <span className={styles['calc-value']}>{formatAmount(requestAmount)} 원</span>
                        </div>
                        <div className={styles['calc-row']}>
                            <span className={styles['calc-label']}>수수료</span>
                            <span className={styles['calc-value']}>0 원</span>
                        </div>
                        <div className={`${styles['calc-row']} ${styles['total']}`}>
                            <span className={styles['calc-label']}>최종 송금액</span>
                            <span className={styles['calc-value']}>{formatAmount(requestAmount)} 원</span>
                        </div>
                    </div>

                    {/* 에러 메시지 */}
                    {transferError && (
                        <div className={styles['error-message']}>
                            <span className={styles['error-message-text']}>{transferError.message}</span>
                        </div>
                    )}

                    {/* 버튼들 */}
                    <div className={styles['action-buttons']}>
                        <button
                            className={`${styles['transfer-btn']} ${styles['primary']}`}
                            type="button"
                            onClick={handleTransferSubmit}
                            disabled={isTransferDisabled}
                        >
                            <i className={`fas fa-exchange-alt ${styles['btn-icon']}`}></i>
                            {isTransferring ? '송금 중...' : '송금하기'}
                        </button>

                        <button
                            className={`${styles['transfer-btn']} ${styles['outline']}`}
                            type="button"
                            onClick={() => navigate('/transactions')}
                        >
                            <i className={`fas fa-list ${styles['btn-icon']}`}></i>
                            입출금 내역보기
                        </button>
                    </div>
                </div>
            </div>

            {/* 하단 네비게이션 */}
            <BottomNavigation currentScreen="transfer" />
        </div>
    );
};

export default TransferPage;
